<script setup lang="ts">
/**
 * 派活表单:内容 / 责任人(必须选到账号,可多选)/ 期限 / 优先级 / 类型 / 备注。
 * 会中「转待办」和这里是同一能力的两个入口——后端同一套投影。
 *
 * 0903 加的几样(任务跟踪表配套):
 *  - 类型:自由文本 + 联想大家已经在用的词,不设字典(Tony:「先不设固定值」);
 *  - 派给自己:自建任务就是派给自己的派活,不另起一套;
 *  - 从问题 / 计划节点发起时带上 reference,跟踪表里看得见挂在哪件事上;
 *  - 多责任人「共同一条」(张浩:零件多、两个人交叉处理):每人各自在「我的工作」里出现、
 *    各自点完成,都完成才算完成;谁改了什么按人留痕。
 */
import { computed, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import PeoplePicker from '@/components/PeoplePicker.vue';
import { useFocusTrap } from '@/composables/useFocusTrap';
import { notify } from '@/services/notice';
import type { TaskTypeSuggestion } from '@/types/tasks';
import { errorMessage } from '@/utils/format';

const props = defineProps<{
  /** 从业务对象处发起时带上下文;全局入口不传,表单里可选挂项目 */
  projectId?: number | null;
  /** 挂接的业务对象(问题 / 计划节点),只做展示和回链 */
  reference?: { type: string; id: string | number; label: string } | null;
}>();
const emit = defineEmits<{ close: []; created: [] }>();

const session = useSession();
const content = ref('');
const ownerIds = ref<number[]>([]);
const dueDate = ref('');
const priority = ref<'critical' | 'high' | 'normal' | 'low'>('normal');
const taskType = ref('');
const note = ref('');
const saving = ref(false);
const typeSuggestions = ref<TaskTypeSuggestion[]>([]);
// 全局入口没有项目上下文时,允许挂到某个项目(可选);从项目 / 对象发起的不用选
const projects = ref<Array<{ id: number; projectNo: string; projectName: string }>>([]);
const pickedProjectId = ref<number | null>(null);
const effectiveProjectId = computed(() => props.projectId ?? pickedProjectId.value);
const selfId = computed(() => session.user.value?.id ?? null);
const includesSelf = computed(() => selfId.value !== null && ownerIds.value.includes(selfId.value));
const onlySelf = computed(() => includesSelf.value && ownerIds.value.length === 1);

// AI 智能填写(2026-08-25):一句话 → 预填内容/责任人/期限,人核对后提交
const smartText = ref('');
const parsing = ref(false);

// 焦点陷阱(P1-07):v-if 挂上即打开——Tab 不出框,Esc 同「取消」,首焦落在「要做什么」。
// 关掉时焦点还给打开前的元素;父级要回到特定按钮的(任务跟踪表「＋ 新增任务」)自己再聚焦。
const dialogEl = ref<HTMLElement | null>(null);
const contentEl = ref<HTMLTextAreaElement | null>(null);
const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
useFocusTrap(dialogEl, {
  active: ref(true),
  // 提交中和「取消」按钮一样不让关
  onEscape: () => { if (!saving.value) emit('close'); },
  restoreFocus: () => opener?.focus(),
  initialFocus: () => contentEl.value,
});

onMounted(async () => {
  // 联想词和项目列表都是锦上添花,拿不到也不该挡住派活
  try {
    const result = await eqsApi.taskTypes();
    typeSuggestions.value = result.types ?? [];
  } catch { /* 联想缺席不影响提交 */ }
  if (props.projectId) return;
  try {
    const portfolio = await eqsApi.portfolio();
    projects.value = (portfolio.projects ?? [])
      .filter((project) => !['closed', 'cancelled', 'deleted', 'archived', '已关闭', '已取消', '已删除', '已归档']
        .includes(String(project.status ?? '').trim()))
      .map((project) => ({ id: Number(project.id), projectNo: project.projectNo, projectName: project.projectName }));
  } catch { /* 项目列表缺席只是不能挂项目 */ }
});

function toggleSelf(): void {
  if (selfId.value === null) return;
  ownerIds.value = includesSelf.value
    ? ownerIds.value.filter((id) => id !== selfId.value)
    : [...ownerIds.value, selfId.value];
}

async function smartFill(): Promise<void> {
  if (smartText.value.trim().length < 5) return;
  parsing.value = true;
  try {
    const result = await eqsApi.parseDelegation(smartText.value.trim());
    const draft = result.draft as {
      content?: string; ownerUserId?: number | null;
      ownerMatch?: string | null; ownerName?: string | null; dueDate?: string | null;
    } | null;
    if (!draft) {
      notify('error', '没解析出任务', '把要做的事、给谁、什么期限说得更明确一点。');
      return;
    }
    content.value = draft.content ?? content.value;
    if (draft.ownerUserId) {
      if (!ownerIds.value.includes(draft.ownerUserId)) ownerIds.value = [...ownerIds.value, draft.ownerUserId];
    } else if (draft.ownerName) {
      notify('error', '责任人要人工选', draft.ownerMatch === 'ambiguous'
        ? `「${draft.ownerName}」有重名,请在下面选具体的人`
        : `「${draft.ownerName}」没对上系统账号,请在下面选人`);
    }
    if (draft.dueDate) dueDate.value = draft.dueDate;
  } catch (caught) {
    notify('error', 'AI 解析失败', errorMessage(caught));
  } finally {
    parsing.value = false;
  }
}

async function submit(): Promise<void> {
  if (content.value.trim().length < 2) {
    notify('error', '派活失败', '先把要做的事写清楚(至少 2 个字)');
    return;
  }
  if (!ownerIds.value.length) {
    notify('error', '派活失败', '至少选一位责任人(只能选系统账号)');
    return;
  }
  if (!dueDate.value) {
    notify('error', '派活失败', '必须给期限——没有期限的任务没人会当真');
    return;
  }
  saving.value = true;
  try {
    await eqsApi.createDelegation({
      content: content.value.trim(),
      ownerUserIds: [...ownerIds.value],
      // 期限统一按当天下班前(17:30,东八区)
      dueAt: `${dueDate.value}T17:30:00+08:00`,
      priority: priority.value,
      ...(effectiveProjectId.value ? { projectId: effectiveProjectId.value } : {}),
      ...(props.reference ? { referenceType: props.reference.type, referenceId: String(props.reference.id) } : {}),
      ...(taskType.value.trim() ? { taskType: taskType.value.trim() } : {}),
      ...(note.value.trim() ? { note: note.value.trim() } : {}),
    });
    notify('success', onlySelf.value ? '已建好' : '已派出', onlySelf.value
      ? '任务已进入你的「我的工作」和任务跟踪表。'
      : ownerIds.value.length > 1
        ? `${ownerIds.value.length} 位责任人各自收到站内通知,各自在「我的工作」里点完成,都完成才算完成。`
        : '对方会收到站内通知,任务已进入其「我的工作」。');
    emit('created');
    emit('close');
  } catch (caught) {
    notify('error', '派活失败', errorMessage(caught));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="dialog-mask" role="dialog" aria-modal="true" aria-label="派活" @click.self="emit('close')">
    <section ref="dialogEl" class="dialog-card">
      <header>
        <span>DELEGATE</span>
        <h2>派活</h2>
        <p>直接生成一条有主、有期限、可追踪的待办;对方在「我的工作」处理,所有人都能在「任务跟踪」看到下场。</p>
        <p v-if="reference" class="reference-line">挂在:{{ reference.label }}</p>
      </header>
      <div class="smart-fill">
        <input
          v-model="smartText"
          maxlength="2000"
          placeholder="试试一句话:让肖娟周五前把报价对比整理出来"
          @keyup.enter="smartFill"
        >
        <button class="inline-button" type="button" :disabled="parsing" @click="smartFill">
          {{ parsing ? '解析中…' : 'AI 填表' }}
        </button>
      </div>
      <label>
        <span>要做什么<i>*</i></span>
        <textarea ref="contentEl" v-model="content" rows="3" maxlength="500" placeholder="一句话说清楚要做的事和验收标准"></textarea>
      </label>
      <label>
        <span class="owner-head">
          责任人<i>*</i>
          <button
            v-if="selfId !== null"
            type="button"
            class="inline-button self-button"
            :aria-pressed="includesSelf"
            @click="toggleSelf"
          >{{ includesSelf ? '已含自己' : '派给自己' }}</button>
        </span>
        <PeoplePicker v-model="ownerIds" :multiple="true" placeholder="搜索姓名 / 工号,可选多人(必须选到账号)" />
        <small class="owner-hint">
          {{ ownerIds.length > 1
            ? `${ownerIds.length} 人共同负责:各自在「我的工作」里出现、各自点完成,都完成才算完成;谁改了什么按人留痕。`
            : '可以再选一位,两个人共同负责这一条。' }}
        </small>
      </label>
      <div class="dialog-row">
        <label>
          <span>期限<i>*</i></span>
          <input v-model="dueDate" type="date">
        </label>
        <label>
          <span>优先级</span>
          <select v-model="priority">
            <option value="normal">普通</option>
            <option value="high">高</option>
            <option value="critical">紧急</option>
            <option value="low">低</option>
          </select>
        </label>
      </div>
      <div class="dialog-row">
        <label>
          <span>类型(可选)</span>
          <input v-model="taskType" list="delegate-task-types" maxlength="40" placeholder="报价 / 回款 / 客户跟进…">
          <datalist id="delegate-task-types">
            <option v-for="item in typeSuggestions" :key="item.value" :value="item.value">{{ item.value }}(用过 {{ item.total }} 次)</option>
          </datalist>
        </label>
        <label v-if="!projectId">
          <span>挂到项目(可选)</span>
          <select v-model="pickedProjectId">
            <option :value="null">不挂项目</option>
            <option v-for="project in projects" :key="project.id" :value="project.id">
              {{ project.projectNo }} {{ project.projectName }}
            </option>
          </select>
        </label>
      </div>
      <label>
        <span>备注(可选)</span>
        <input v-model="note" maxlength="500" placeholder="背景、资料位置、注意事项">
      </label>
      <footer>
        <button class="button button-secondary" type="button" :disabled="saving" @click="emit('close')">取消</button>
        <button class="button button-primary" type="button" :disabled="saving" @click="submit">
          {{ saving ? '提交中…' : onlySelf ? '建给自己' : ownerIds.length > 1 ? `派给 ${ownerIds.length} 人` : '派出' }}
        </button>
      </footer>
    </section>
  </div>
</template>

<style scoped>
.dialog-mask {
  position: fixed; inset: 0; z-index: 60; display: grid; place-items: center;
  background: rgba(15, 43, 82, .32); padding: 20px;
}
.dialog-card {
  width: min(560px, 100%); display: grid; gap: 14px; padding: 22px 24px 20px;
  background: var(--white); border: 1px solid var(--line); border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 43, 82, .22);
  max-height: calc(100vh - 40px); overflow-y: auto;
}
.dialog-card header span { color: var(--blue); font: 800 12px var(--display); letter-spacing: .12em; }
.dialog-card header h2 { margin: 2px 0 6px; }
.dialog-card header p { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.6; }
.dialog-card header .reference-line { margin-top: 6px; color: var(--ink); font-weight: 700; }
.dialog-card label { display: grid; gap: 6px; font-size: 13px; font-weight: 700; }
.dialog-card label i { color: var(--risk); font-style: normal; }
.owner-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.owner-hint { color: var(--muted); font-size: 12px; font-weight: 400; line-height: 1.5; }
.self-button { font-weight: 600; }
.dialog-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.dialog-card footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 4px; }
.smart-fill { display: flex; gap: 8px; padding: 8px 10px; border: 1px dashed var(--line-strong); border-radius: 10px; background: var(--paper); }
.smart-fill input { flex: 1; border: 0; background: transparent; }
.smart-fill input:focus { outline: none; }
@media (max-width: 640px) { .dialog-row { grid-template-columns: 1fr; } }
</style>
